import { BuildingEnergyModelFragment } from '../../../generated/graphql'
import {
  averageByEvenNumberRange,
  averageByHour,
  dayHours,
  hoursOfTheDay,
  monthsSumSerie,
  rangeValues,
  tempCutSerie,
  yearMonths,
} from './calibration-util'
import {
  calcDhwConsumptionDensityHourSeries,
  calcDhwCirculationLossDensity,
} from '../../../energy-system/dhw-hour-series-calculator'
import { i18n } from '../../../i18n'
import {
  createChartSerie,
  createChartSerieValues,
  createDefaultLineChartSerie,
  CustomLineStyles,
  CustomSeries,
} from './chart-util'
import { hoursInYear } from '../../../util/hours-in-year'

const DEFAULT_DHW_LINETYPE = CustomLineStyles.Solid
const DEFAULT_DHW_LINEWIDTH = 0.1

export const calcDhwCirculationLossChartData = (
  model: BuildingEnergyModelFragment,
  rangeStart = 0,
  rangeEnd = 23,
  rangeStep = 1,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.dhwCirculation')

  const dhwRange = rangeValues(rangeStart, rangeEnd, rangeStep)

  const result = createDefaultLineChartSerie(
    id,
    dhwRange,
    CustomLineStyles.Solid,
    0.1,
    true,
  )

  const dhwValue = calcDhwCirculationLossDensity(model, model.area)

  result.data = createChartSerieValues(
    dhwRange,
    Array<number>(dhwRange.length).fill(dhwValue),
  )

  return result
}

export const calcDhwConsumtionByHourChartData = (
  model: BuildingEnergyModelFragment,
  cutSerie: number[],
  year: number,
): CustomSeries => {
  const hourValues = dayHours()

  const hours = hoursOfTheDay(0)

  const dhwConsumptionData = averageByHour(
    hourValues,
    calcDhwConsumptionDensityHourSeries(model, year),
    cutSerie,
    hours,
  )

  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.dhwHeating')

  return createChartSerie(
    id,
    hourValues,
    dhwConsumptionData,
    DEFAULT_DHW_LINETYPE,
    DEFAULT_DHW_LINEWIDTH,
    true,
  )
}

export const calcDhwConsumtionInSummerByHourChartData = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
  year: number,
): CustomSeries => {
  const summer = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.summerTempStart ?? 100,
    model.calibrationLimitValues?.summerTempEnd ?? -100,
  )

  return calcDhwConsumtionByHourChartData(model, summer, year)
}

export const calcDhwConsumtionInZeroTempByHourChartData = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
  year: number,
): CustomSeries => {
  const cutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.zeroWeatherTempStart ?? 100,
    model.calibrationLimitValues?.zeroWeatherTempEnd ?? -100,
  )

  return calcDhwConsumtionByHourChartData(model, cutSerie, year)
}

export const calcDhwConsumtionByEvenTempRangeChartData = (
  model: BuildingEnergyModelFragment,
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
  cutSerie: number[],
  year: number,
): CustomSeries => {
  const temperatureValues = rangeValues(
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
  )

  const dhwConsumptionData = averageByEvenNumberRange(
    temperatureValues,
    model.calOutdoorHourSeries?.values ?? [],
    calcDhwConsumptionDensityHourSeries(model, year),
    cutSerie,
  )

  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.dhwHeating')

  return createChartSerie(
    id,
    temperatureValues,
    dhwConsumptionData,
    DEFAULT_DHW_LINETYPE,
    DEFAULT_DHW_LINEWIDTH,
    true,
  )
}

export const calcSumDhwCirculationLossByMonthChartData = (
  model: BuildingEnergyModelFragment,
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.dhwCirculation')

  const monthValues = yearMonths()

  const dhwValue = calcDhwCirculationLossDensity(model, model.area)

  const dhwCirculationLossData = monthsSumSerie(
    Array<number>(hoursInYear).fill(dhwValue),
    year,
  )

  return createChartSerie(
    id,
    monthValues,
    dhwCirculationLossData,
    DEFAULT_DHW_LINETYPE,
    DEFAULT_DHW_LINEWIDTH,
    true,
  )
}

export const calcSumDhwConsumtionByMonthChartData = (
  model: BuildingEnergyModelFragment,
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.dhwHeating')

  const monthValues = yearMonths()

  const dhwConsumptionDensitySerie = calcDhwConsumptionDensityHourSeries(
    model,
    year,
  )

  const dhwConsumptionData = monthsSumSerie(dhwConsumptionDensitySerie, year)

  return createChartSerie(
    id,
    monthValues,
    dhwConsumptionData,
    DEFAULT_DHW_LINETYPE,
    DEFAULT_DHW_LINEWIDTH,
    true,
  )
}

export const calcSumDhwHeatingInYearByMonth = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries[] => {
  const dhwCirculationLossDensitySerie =
    calcSumDhwCirculationLossByMonthChartData(model, year)

  const dhwConsumtionDensitySerie = calcSumDhwConsumtionByMonthChartData(
    model,
    year,
  )

  return [dhwCirculationLossDensitySerie, dhwConsumtionDensitySerie]
}
